type FrontendModuleName =
    | 'bom-section'
    | 'collections-page'
    | 'colours-card'
    | 'control-page'
    | 'create-orders-card'
    | 'create-sample-orders-card'
    | 'ext-comp-page'
    | 'order-card'
    | 'purchase-orders-page'
    | 'reports-page'
    | 'sample-order-card'
    | 'sample-orders-page'
    | 'settings-page'
    | 'style-card'
    | 'styles-page';

/**
 * @name The id of the frontend module
 * @port The port to use for local development
 * @fileName The filename of the exported javascript bundle
 * @urlPrefix What to prefix the deployment with on the cluster
 */
interface FrontendModuleConfig {
    port: string | undefined;
    fileName: string;
    urlPrefix: string;
}

const frontendModuleConfigs: Record<FrontendModuleName, FrontendModuleConfig> =
    {
        ['bom-section']: {
            port: process.env.BOM_SECTION_PORT,
            fileName: 'bomSectionEntry.js',
            urlPrefix: 'bom-section',
        },
        ['collections-page']: {
            port: process.env.COLLECTIONS_PAGE_PORT,
            fileName: 'collectionsPageEntry.js',
            urlPrefix: 'collections-page',
        },
        ['colours-card']: {
            port: process.env.COLLECTION_COLOURS_CARD_PORT,
            fileName: 'coloursCardEntry.js',
            urlPrefix: 'colours-card',
        },
        ['control-page']: {
            port: process.env.CONTROL_PAGE_PORT,
            fileName: 'controlPageEntry.js',
            urlPrefix: 'control-page',
        },
        ['create-orders-card']: {
            port: process.env.CREATE_ORDERS_CARD_PORT,
            fileName: 'createOrdersCardEntry.js',
            urlPrefix: 'create-orders-card',
        },
        ['create-sample-orders-card']: {
            port: process.env.CREATE_SAMPLE_ORDERS_CARD_PORT,
            fileName: 'createSampleOrdersCardEntry.js',
            urlPrefix: 'create-sample-orders-card',
        },
        ['ext-comp-page']: {
            port: process.env.EXTENDED_COMPOSITION_PAGE_PORT,
            fileName: 'extendedCompositionPageEntry.js',
            urlPrefix: 'ext-comp-page',
        },
        ['order-card']: {
            port: process.env.ORDER_CARD_PORT,
            fileName: 'orderCardEntry.js',
            urlPrefix: 'order-card',
        },
        ['purchase-orders-page']: {
            port: process.env.PURCHASE_ORDERS_PAGE_PORT,
            fileName: 'purchaseOrdersPageEntry.js',
            urlPrefix: 'purchase-orders-page',
        },
        ['reports-page']: {
            port: process.env.REPORTS_PAGE_PORT,
            fileName: 'reportsPageEntry.js',
            urlPrefix: 'reports-page',
        },
        ['sample-order-card']: {
            port: process.env.SAMPLE_ORDER_CARD_PORT,
            fileName: 'sampleOrderCardEntry.js',
            urlPrefix: 'sample-order-card',
        },
        ['sample-orders-page']: {
            port: process.env.SAMPLE_ORDERS_PAGE_PORT,
            fileName: 'sampleOrdersPageEntry.js',
            urlPrefix: 'sample-orders-page',
        },
        ['settings-page']: {
            port: process.env.SETTINGS_PAGE_PORT,
            fileName: 'settingsPageEntry.js',
            urlPrefix: 'settings-page',
        },
        ['style-card']: {
            port: process.env.STYLE_CARD_PORT,
            fileName: 'styleCardEntry.js',
            urlPrefix: 'style-card',
        },
        ['styles-page']: {
            port: process.env.STYLES_PAGE_PORT,
            fileName: 'stylesPageEntry.js',
            urlPrefix: 'styles-page',
        },
    };

/**
 * @description Generates the URL of an entry file, depending on the window.location.hostname
 * @param {*} moduleId - The id of the module, used for targeting the right configuration
 * @param {*} branch - The branch used when hosted remotely. Defaults to `window.env.ENTRY_PREFIX`
 *   which is the deployment prefix shared by all microfrontends in the same pod.
 *   Pass an explicit empty string (`''`) to force the master (prefix-less) URL.
 * @returns The URL of the entry file
 * @example:
 *
 * Local:
 * http://localhost:3001/bomSectionEntry.js
 *
 * Dev (master):
 * https://buying-dev.bestseller.com/bom-section/bomSectionEntry.js
 *
 * Dev (BUY-1234):
 * https://buying-dev.bestseller.com/BUY-1234/bom-section/bomSectionEntry.js
 */
const generateEntryFileUrl = (
    moduleId: FrontendModuleName,
    branch = window.env?.ENTRY_PREFIX ?? ''
): string => {
    const config = frontendModuleConfigs[moduleId];
    return window.location.hostname === 'localhost'
        ? `http://localhost:${config.port}/${config.fileName}`
        : `${window.location.origin}${branch}/${config.urlPrefix}/${config.fileName}`;
};

export { generateEntryFileUrl };
export type { FrontendModuleName };
